import React, { useCallback, useState, useEffect, useRef, useMemo } from "react"
import ReactMapGL, { MapRef, NavigationControl, FullscreenControl, ViewStateChangeEvent, Popup, Source, Layer } from "react-map-gl"
import "mapbox-gl/dist/mapbox-gl.css"
import MarkerLayer from "./markers/MarkerLayer"
import Boundary from "./Boundary"
import { DEFAULT_STYLES, MAPBOX_TOKEN, MAX_MARKERS_PER_TYPE, getMapStyle } from "./constants"
import type { MapProps, Place } from "../../types"
import { getClusters, Feature } from "vibemap-constants/utils/map.js"
import { LngLatBounds } from "mapbox-gl"
import "./styles/Heatmap.scss"
import { heatmapLayer, createHeatmapSource } from "./Heatmap"

const decodeHtmlEntities = (text: string): string => {
  const textarea = document.createElement("textarea")
  textarea.innerHTML = text
  return textarea.value
}

const getClusterSize = (zoom: number): number => {
  if (zoom >= 14) return 10
  if (zoom >= 12) return 50
  if (zoom >= 10) return 100
  return 200
}

const hasClusterId = (place: Feature): place is Feature & { properties: { cluster_id: string } } => {
  return typeof place.properties.cluster_id === "string"
}

const Map: React.FC<MapProps> = ({
  mapRef,
  viewport: initialViewport,
  onViewportChange,
  mapStyle = DEFAULT_STYLES.LIGHT,
  places = [],
  selectedPlace,
  onPlaceSelect,
  markerStyle = "images",
  showMarkers = true,
  showBoundary = false,
  boundaryCoordinates,
  onMarkerHover,
  onMarkerLeave,
  children,
  minZoom = 0,
  maxZoom = 20,
  showFullScreen = true,
  showNavigation = true,
  cooperativeGestures = false,
  dragPan = true,
  scrollZoom = true,
  initialClusterSize = 200,
  shouldCluster = true,
  showClusterSize = false,
  vibes = [],
  categories = [],
  onChange,
  showHeat = false,
  heatmapData,
}) => {
  const mapReference = mapRef || React.useRef<MapRef>(null)
  const [placeMarkers, setPlaceMarkers] = useState<Place[]>([])
  const [viewport, setViewport] = useState({
    latitude: initialViewport?.latitude ?? boundaryCoordinates?.[0]?.[1] ?? 0,
    longitude: initialViewport?.longitude ?? boundaryCoordinates?.[0]?.[0] ?? 0,
    zoom: (initialViewport?.zoom ?? 14) - 2,
    bearing: initialViewport?.bearing ?? 0,
    pitch: initialViewport?.pitch ?? 0,
  })
  const [popupInfo, setPopupInfo] = useState<{
    latitude: number
    longitude: number
    name: string
    isSelected?: boolean
  } | null>(null)

  const boundsRef = useRef<[number, number, number, number] | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const handleViewportChange = useCallback(
    (evt: ViewStateChangeEvent) => {
      const newViewport = evt.viewState
      setViewport(newViewport)
      onViewportChange?.(newViewport)
    },
    [onViewportChange]
  )

  const isPointInBoundary = useCallback(
    (coordinates: [number, number]) => {
      if (!boundaryCoordinates) return true

      const bounds = new LngLatBounds()
      boundaryCoordinates.forEach((coord) => bounds.extend(coord))

      return bounds.contains(coordinates)
    },
    [boundaryCoordinates]
  )

  const clusterSize = useMemo(() => {
    if (!viewport?.zoom) return initialClusterSize
    return getClusterSize(viewport.zoom)
  }, [viewport?.zoom, initialClusterSize])

  const heatmapSource = useMemo(() => {
    if (!showHeat || !heatmapData) return null
    return createHeatmapSource(heatmapData)
  }, [showHeat, heatmapData])

  useEffect(() => {
    if (!places.length || !viewport) return

    try {
      const boundaryFiltered = places
        .filter((place) => isPointInBoundary(place.geometry.coordinates))
        .sort((a, b) => (b.properties.score_combined || 0) - (a.properties.score_combined || 0))

      const usedVibeIds = new Set()
      const usedCategoryIds = new Set()

      boundaryFiltered.forEach((place) => {
        place.properties.vibes?.forEach((vibe) => {
          usedVibeIds.add(vibe.id.toString())
        })
        place.properties.categories?.forEach((cat) => {
          usedCategoryIds.add(cat.id.toString())
        })
      })

      onChange?.({
        vibes: vibes?.filter((vibe) => usedVibeIds.has(vibe.id.toString())) || [],
        categories: categories?.filter((category) => usedCategoryIds.has(category.id.toString())) || [],
      })

      const imageMarkers = boundaryFiltered
        .filter((place) => getPlaceImage(place.properties))
        .slice(0, MAX_MARKERS_PER_TYPE)
        .map((place) => ({
          ...place,
          properties: {
            ...place.properties,
            markerType: "image",
          },
        })) as Place[]

      const imageMarkerIds = new Set(imageMarkers.map((p) => p.id))
      const remainingPlaces = boundaryFiltered.filter((place) => !imageMarkerIds.has(place.id))

      const iconMarkers = remainingPlaces.slice(0, MAX_MARKERS_PER_TYPE).map((place) => ({
        ...place,
        properties: {
          ...place.properties,
          markerType: "icon",
        },
      })) as Place[]

      let processedPlaces = [...imageMarkers, ...iconMarkers]

      if (selectedPlace && !processedPlaces.find((p) => p.id === selectedPlace.id)) {
        processedPlaces = [
          ...processedPlaces,
          {
            ...selectedPlace,
            properties: {
              ...selectedPlace.properties,
              markerType: getPlaceImage(selectedPlace.properties) ? "image" : "icon",
              top_in_cluster: true,
              in_cluster: false,
            },
          } as Place,
        ]
      }

      if (shouldCluster && viewport.zoom < 14) {
        const imageMarkers = processedPlaces.filter((place) => getPlaceImage(place.properties))
        const iconMarkers = processedPlaces.filter((place) => !getPlaceImage(place.properties))

        const clusteredImageMarkers = getClusters(imageMarkers as Feature[], clusterSize)
        const clusteredIconMarkers = getClusters(iconMarkers as Feature[], clusterSize)

        const processCluster = (features: Feature[], isImageMarker: boolean) => {
          const clusters = features.reduce<Record<string, Feature[]>>((acc, place) => {
            if (!hasClusterId(place)) return acc
            const clusterId = place.properties.cluster_id
            if (!acc[clusterId]) acc[clusterId] = []
            acc[clusterId].push(place)
            return acc
          }, {})

          return features.map((place) => {
            const isSelected = selectedPlace && place.id === selectedPlace.id

            if (!hasClusterId(place) || isSelected) {
              return {
                ...place,
                properties: {
                  ...place.properties,
                  top_in_cluster: true,
                  in_cluster: false,
                  cluster_size: 1,
                },
              }
            }

            const clusterId = place.properties.cluster_id
            const cluster = clusters[clusterId]
            const isTop = cluster && cluster[0].id === place.id

            return {
              ...place,
              properties: {
                ...place.properties,
                top_in_cluster: isTop || isSelected,
                in_cluster: !isSelected,
                cluster_size: cluster?.length || 1,
              },
            }
          })
        }

        const processedImageMarkers = processCluster(clusteredImageMarkers, true)
        const processedIconMarkers = processCluster(clusteredIconMarkers, false)

        processedPlaces = [...processedImageMarkers, ...processedIconMarkers] as Place[]
      } else {
        processedPlaces = processedPlaces.map((place) => ({
          ...place,
          properties: {
            ...place.properties,
            top_in_cluster: true,
            in_cluster: false,
            cluster_size: 1,
          },
        }))
      }

      setPlaceMarkers(processedPlaces)
    } catch (error) {
      console.error("Error processing places:", error)
      setPlaceMarkers([])
    }
  }, [places, boundaryCoordinates, viewport?.zoom, selectedPlace, shouldCluster])

  useEffect(() => {
    const map = mapReference.current?.getMap()
    if (!map) return

    const handleMapLoad = () => {
      const bounds = map.getBounds()
      if (bounds) {
        const sw = bounds.getSouthWest()
        const ne = bounds.getNorthEast()
        boundsRef.current = [sw.lng, sw.lat, ne.lng, ne.lat]
      }
    }

    map.once("load", handleMapLoad)
    return () => {
      map.off("load", handleMapLoad)
    }
  }, [])

  const theMapStyle = getMapStyle(mapStyle)

  const handleMarkerClick = useCallback(
    (place: Place) => {
      onPlaceSelect?.(place)
    },
    [onPlaceSelect]
  )

  const handleMarkerHover = useCallback(
    (place: Place) => {
      const [longitude, latitude] = place.geometry.coordinates
      setPopupInfo({
        latitude,
        longitude,
        name: place.properties.name,
      })
      onMarkerHover?.(place)
    },
    [onMarkerHover]
  )

  const handleMarkerLeave = useCallback(
    (place: Place) => {
      if (!selectedPlace) {
        setPopupInfo(null)
      }
      onMarkerLeave?.(place)
    },
    [onMarkerLeave, selectedPlace]
  )

  useEffect(() => {
    if (selectedPlace) {
      const [longitude, latitude] = selectedPlace.geometry.coordinates
      setPopupInfo({
        latitude,
        longitude,
        name: selectedPlace.properties.name,
        isSelected: true,
      })

      const map = mapReference.current?.getMap()
      if (map) {
        map.flyTo({
          center: [longitude, latitude],
          zoom: map.getZoom(),
          duration: 500,
        })
      }
    } else {
      setPopupInfo(null)
    }
  }, [selectedPlace])

  const fitMapToBoundary = useCallback(() => {
    const map = mapReference.current?.getMap()
    if (!map || !boundaryCoordinates?.length) return

    const bounds = new LngLatBounds()
    boundaryCoordinates.forEach((coord) => bounds.extend(coord))

    map.fitBounds(bounds, {
      padding: 40,
      duration: 2000,
      essential: true,
      linear: false,
      easing: (t) => {
        return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2
      },
    })
  }, [boundaryCoordinates])

  useEffect(() => {
    const map = mapReference.current?.getMap()
    if (!map) return

    map.once("load", () => {
      map.setZoom(12)
      setTimeout(() => {
        fitMapToBoundary()
      }, 100)
    })
  }, [fitMapToBoundary])

  const getPlaceImage = (properties: any): string | undefined => {
    return (
      properties.image ||
      properties.thumbnail_url ||
      (properties.vibemap_images && properties.vibemap_images[0]?.thumbnail_url) ||
      (properties.vibemap_images && typeof properties.vibemap_images[0] === "string" && properties.vibemap_images[0])
    )
  }

  useEffect(() => {
    if (!containerRef.current || !mapRef?.current) return

    const resizeObserver = new ResizeObserver(() => {
      if (mapRef.current) {
        mapRef.current.resize()
      }
    })

    resizeObserver.observe(containerRef.current)
    return () => resizeObserver.disconnect()
  }, [mapRef])

  useEffect(() => {
    requestAnimationFrame(() => {
      if (mapRef?.current) {
        mapRef.current.resize()
      }
    })
  }, [])

  return (
    <div ref={containerRef} className="map-container" style={{ position: "relative", height: "100%", width: "100%", maxWidth: "100%" }}>
      <ReactMapGL
        ref={mapReference}
        mapboxAccessToken={MAPBOX_TOKEN}
        initialViewState={viewport}
        onMove={handleViewportChange}
        style={{ width: "100%", height: "100%", maxWidth: "100%" }}
        mapStyle={theMapStyle}
        cooperativeGestures={cooperativeGestures}
        dragPan={dragPan}
        scrollZoom={scrollZoom}
        minZoom={minZoom}
        maxZoom={maxZoom}
        reuseMaps
      >
        {heatmapSource && (
          <Source {...heatmapSource}>
            <Layer {...heatmapLayer} beforeId="waterway-label" />
          </Source>
        )}

        {showNavigation && <NavigationControl position="bottom-right" />}
        {showFullScreen && <FullscreenControl position="bottom-right" />}

        {showBoundary && boundaryCoordinates && <Boundary coordinates={boundaryCoordinates} />}

        {showMarkers && (
          <MarkerLayer
            places={placeMarkers}
            selectedPlace={selectedPlace}
            onMarkerClick={handleMarkerClick}
            onMarkerHover={handleMarkerHover}
            onMarkerLeave={handleMarkerLeave}
            markerStyle={markerStyle}
            showClusterSize={showClusterSize}
          />
        )}

        {popupInfo && (
          <Popup longitude={popupInfo.longitude} latitude={popupInfo.latitude} anchor="bottom" closeButton={false} closeOnClick={false} offset={40}>
            <div className="marker-popup">{decodeHtmlEntities(popupInfo.name)}</div>
          </Popup>
        )}

        {children}
      </ReactMapGL>
    </div>
  )
}

export default Map
